import { EntityTypes } from "../../EntityTypes";
import { IFactoryResource } from "../../factories";
import { ILanguageResource } from "../../languages";
import { ILocationResource } from "../../locations";
import { createApiMutationHook, IApiMutationFunction } from "../../utils/api";
import { mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";
import { getResourceUuid } from "../../utils/getResourceUuid";
import { IClassroom } from "../types/IClassroom";

export type IClassroomUpdateWithTemplateActionParameters = {
    rank: number;
    erpIdentifier: string;
    erpFile?: File | null;
};

export type IClassroomUpdateWithTemplateMutationData = Partial<
    Pick<IClassroom, "title" | "description" | "dailyStartHour" | "dailyEndHour" | "isBadgesEnabled">
> & {
    location?: RelationalValue<ILocationResource>;
    language?: RelationalValue<ILanguageResource>;
    factory?: RelationalValue<IFactoryResource>;
    image?: RelationalValue<NonNullable<IClassroom["image"]>>;
    trainingActions?: IClassroomUpdateWithTemplateActionParameters[];
};

export type IClassroomUpdateWithTemplateMutationParameters = {
    classroom: RelationalValue<IClassroom>;
    data: IClassroomUpdateWithTemplateMutationData;
};

export type IClassroomUpdateWithTemplateMutationResult = IClassroom;

type TPayloadValue = string | boolean | undefined | null;

export const classroomUpdateWithTemplateMutation: IApiMutationFunction<
    IClassroomUpdateWithTemplateMutationParameters
> = ({ classroom, data }) => {
    const { location, language, factory, image, trainingActions, ...scalarFields } = data;

    const payload: Record<string, TPayloadValue> = {
        ...scalarFields,
        location: mapRelationalValueAsIRI(EntityTypes.LOCATION, location),
        language: mapRelationalValueAsIRI(EntityTypes.LANGUAGE, language),
        factory: mapRelationalValueAsIRI(EntityTypes.FACTORY, factory),
        image: mapRelationalValueAsIRI(EntityTypes.MEDIA, image),
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
        if (undefined === value || null === value) {
            return;
        }

        formData.append(key, value.toString());
    });

    (trainingActions ?? []).forEach((action, index) => {
        formData.append(`trainingActions[${index}][rank]`, String(action.rank));
        formData.append(`trainingActions[${index}][erpIdentifier]`, action.erpIdentifier);

        if (action.erpFile instanceof File) {
            formData.append(`trainingActions[${index}][erpFile]`, action.erpFile);
        }
    });

    return {
        method: "PATCH",
        url: `/classrooms/${getResourceUuid(classroom)}`,
        headers: {
            "Content-Type": "multipart/form-data",
        },
        data: formData,
    };
};

export const useClassroomUpdateWithTemplateMutation = createApiMutationHook<
    IClassroomUpdateWithTemplateMutationParameters,
    IClassroomUpdateWithTemplateMutationResult
>(classroomUpdateWithTemplateMutation);
